/* ══════════════════════════════════════════════════════════════════════════
   KITSTERS - MARQUEE MEDIA LOADER
   --------------------------------------------------------------------------
   Loads a marquee's images only once the strip is near the viewport.

   WHY A MARQUEE CANNOT JUST USE loading="lazy" (measured, twice):
     · Its track is `width: max-content` and several screens wide, so most
       cards sit far outside the viewport HORIZONTALLY and are never fetched -
       and a CSS transform does not re-trigger the lazy check, because the
       element's LAYOUT position never changes, only its paint. Measured on the
       Corporate logo wall before it went eager: 24 of 68 images with
       naturalWidth 0, i.e. permanent blank holes.
     · So both marquees ship their images EAGER, which is correct for the
       cards but means every image in the strip is fetched on first paint even
       though the strip is below the fold. Measured on the Magic Hour landing
       page: 93 KB across 24 requests for a logo wall the shopper has not
       scrolled to.

   This resolves both: the caller emits `data-src` / `data-srcset` and no
   `src`, and everything in a row is promoted together the moment that row
   comes within 400px of the viewport. Nothing is deferred once the row is
   reached, so the horizontal problem above never applies.

   FAILURE BEHAVIOUR IS THE WHOLE DESIGN. An image with only `data-src` is
   invisible if this file never runs, so:
     · with no IntersectionObserver at all, a throttled scroll/resize listener
       runs the same near-viewport test;
     · a one-shot re-check at 3s catches an observer that exists but was never
       fed (an ancestor with `content-visibility`, a browser quirk);
     · the promotion is idempotent, so several triggers cost nothing.
   The 3s belt deliberately re-tests POSITION rather than promoting everything.
   An unconditional timer was the first cut and it silently undid the whole
   optimisation - measured, the Magic Hour logo wall still pulled its full
   93 KB because the timer fired before the shopper had scrolled anywhere near
   it. A belt that defeats the thing it is protecting is not a belt.
   Only a total failure to LOAD this file loses the images, which is why it is
   ~60 lines with no dependencies rather than part of a page bundle.

   Emitted by snippets/kitsters-photo-marquee.liquid and
   snippets/kitsters-logo-wall.liquid. Both may render on one page, so the
   whole thing is guarded and idempotent.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.__kitstersMarqueeMedia) return;
  window.__kitstersMarqueeMedia = true;

  function promote(row) {
    if (!row || row.dataset.kmmDone === '1') return;
    row.dataset.kmmDone = '1';
    row.querySelectorAll('img[data-src]').forEach(function (im) {
      var ss = im.getAttribute('data-srcset');
      // srcset BEFORE src: with both set the browser chooses from srcset, and
      // assigning src first can start a second, wasted request for it.
      if (ss) { im.srcset = ss; im.removeAttribute('data-srcset'); }
      im.src = im.getAttribute('data-src');
      im.removeAttribute('data-src');
    });
  }

  var MARGIN = 400;   // start the fetch this far before the row is on screen

  function near(row) {
    var r = row.getBoundingClientRect();
    return r.top < window.innerHeight + MARGIN && r.bottom > -MARGIN;
  }

  function sweep(rows) {
    rows.forEach(function (row) { if (near(row)) promote(row); });
  }

  function init() {
    var rows = [].slice.call(document.querySelectorAll('[data-marquee-media]'));
    if (!rows.length) return;

    if (!('IntersectionObserver' in window)) {
      var ticking = false;
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { ticking = false; sweep(rows); });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      sweep(rows);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        promote(e.target);
        io.unobserve(e.target);
      });
    }, { rootMargin: MARGIN + 'px 0px' });
    rows.forEach(function (r) { io.observe(r); });

    // Belt: re-test POSITION once, in case the observer exists but was never
    // fed. Promoting unconditionally here would undo the optimisation.
    setTimeout(function () { sweep(rows); }, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
